import Box from '@mui/material/Box';
import { pxToRem } from '../../../utils/pxToRem';

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type Inset = { horizontal: number; vertical: number };
type Responsive<T> = T | { mobile: T; laptop: T };

type CornerFrameProps = {
  corner: Responsive<Corner>;
  inset: Responsive<Inset>;
  radius: Responsive<number>;
};

type HLineStyles = { top: string | number; bottom: string | number };
type VLineStyles = { left: string | number; right: string | number };
type ArcStyles = {
  top: string | number;
  right: string | number;
  bottom: string | number;
  left: string | number;
  width: string;
  height: string;
  borderTop: string;
  borderRight: string;
  borderBottom: string;
  borderLeft: string;
  borderTopLeftRadius: string | number;
  borderTopRightRadius: string | number;
  borderBottomLeftRadius: string | number;
  borderBottomRightRadius: string | number;
};

function asResponsive<T>(value: Responsive<T>): { mobile: T; laptop: T } {
  if (
    typeof value === 'object' &&
    value !== null &&
    'mobile' in (value as object) &&
    'laptop' in (value as object)
  ) {
    return value as { mobile: T; laptop: T };
  }
  return { mobile: value as T, laptop: value as T };
}

function hLine(corner: Corner, inset: Inset): HLineStyles {
  const isTop = corner === 'top-left' || corner === 'top-right';
  return {
    top: isTop ? pxToRem(inset.vertical) : 'auto',
    bottom: isTop ? 'auto' : pxToRem(inset.vertical),
  };
}

function vLine(corner: Corner, inset: Inset): VLineStyles {
  const isLeft = corner === 'top-left' || corner === 'bottom-left';
  return {
    left: isLeft ? pxToRem(inset.horizontal) : 'auto',
    right: isLeft ? 'auto' : pxToRem(inset.horizontal),
  };
}

function arc(corner: Corner, inset: Inset, radius: number): ArcStyles {
  const stroke = `${pxToRem(1)} solid`;
  const none = '0 solid';
  const r = pxToRem(radius);
  const h = pxToRem(inset.horizontal);
  const v = pxToRem(inset.vertical);
  const size = pxToRem(radius);

  const isTop = corner === 'top-left' || corner === 'top-right';
  const isLeft = corner === 'top-left' || corner === 'bottom-left';

  return {
    top: isTop ? v : 'auto',
    bottom: isTop ? 'auto' : v,
    left: isLeft ? h : 'auto',
    right: isLeft ? 'auto' : h,
    width: size,
    height: size,
    borderTop: isTop ? stroke : none,
    borderBottom: isTop ? none : stroke,
    borderLeft: isLeft ? stroke : none,
    borderRight: isLeft ? none : stroke,
    borderTopLeftRadius: corner === 'top-left' ? r : 0,
    borderTopRightRadius: corner === 'top-right' ? r : 0,
    borderBottomLeftRadius: corner === 'bottom-left' ? r : 0,
    borderBottomRightRadius: corner === 'bottom-right' ? r : 0,
  };
}

export function CornerFrame({ corner, inset, radius }: CornerFrameProps) {
  const c = asResponsive(corner);
  const i = asResponsive(inset);
  const r = asResponsive(radius);

  const hMob = hLine(c.mobile, i.mobile);
  const hLap = hLine(c.laptop, i.laptop);
  const vMob = vLine(c.mobile, i.mobile);
  const vLap = vLine(c.laptop, i.laptop);
  const aMob = arc(c.mobile, i.mobile, r.mobile);
  const aLap = arc(c.laptop, i.laptop, r.laptop);

  const stroke1px = pxToRem(1);

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          backgroundColor: 'secondary.main',
          left: 0,
          right: 0,
          height: stroke1px,
          top: { mobile: hMob.top, laptop: hLap.top },
          bottom: { mobile: hMob.bottom, laptop: hLap.bottom },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          backgroundColor: 'secondary.main',
          top: 0,
          bottom: 0,
          width: stroke1px,
          left: { mobile: vMob.left, laptop: vLap.left },
          right: { mobile: vMob.right, laptop: vLap.right },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          color: 'secondary.main',
          top: { mobile: aMob.top, laptop: aLap.top },
          right: { mobile: aMob.right, laptop: aLap.right },
          bottom: { mobile: aMob.bottom, laptop: aLap.bottom },
          left: { mobile: aMob.left, laptop: aLap.left },
          width: { mobile: aMob.width, laptop: aLap.width },
          height: { mobile: aMob.height, laptop: aLap.height },
          borderTop: { mobile: aMob.borderTop, laptop: aLap.borderTop },
          borderRight: { mobile: aMob.borderRight, laptop: aLap.borderRight },
          borderBottom: { mobile: aMob.borderBottom, laptop: aLap.borderBottom },
          borderLeft: { mobile: aMob.borderLeft, laptop: aLap.borderLeft },
          borderTopLeftRadius: { mobile: aMob.borderTopLeftRadius, laptop: aLap.borderTopLeftRadius },
          borderTopRightRadius: { mobile: aMob.borderTopRightRadius, laptop: aLap.borderTopRightRadius },
          borderBottomLeftRadius: { mobile: aMob.borderBottomLeftRadius, laptop: aLap.borderBottomLeftRadius },
          borderBottomRightRadius: { mobile: aMob.borderBottomRightRadius, laptop: aLap.borderBottomRightRadius },
        }}
      />
    </>
  );
}
